/**
 * LOS OBJETIVOS DEL CLIENTE (doc de Jesús del 2-09, fase 2). Espejo de
 * `backend/core/objetivos.py`, que es la fuente: si se amplía la lista, se amplía en los dos.
 *
 * Los pone el ENTRENADOR, no el cliente (Jesús: «es un cambio de fondo, no de copy»). Dos
 * niveles: el del ciclo (`profile.ciclo_actual.objetivo`) y el actual
 * (`profile.objetivo_actual`), más el `foco` («tonificación con foco glúteo» son dos campos).
 * El `goal` viejo (volumen / definicion) sigue existiendo para el motor de macros; aquí no
 * se pinta.
 *
 * `definicion` ES EL CRITERIO DEL ENTRENADOR Y NO SE LE ENSEÑA AL CLIENTE (ficha 13 del doc
 * del 5-09). «Bajar del 14 % de grasa.» estuvo debajo del objetivo en la pantalla del
 * cliente desde el 4-09; Jesús: «el 14 o el 15 es mi criterio de trabajo, no el objetivo del
 * cliente», y el % de grasa es opcional en la app y se lo mide él, así que el cliente no
 * tiene forma de saber si está en el 14. Solo la pinta el panel
 * (`components/panel/ObjetivoDelCliente`), que es de quien es el criterio.
 */
// El mismo umbral que `core/objetivos.py` (Francisco, 14-09). Escrito una vez y usado en la
// frase, para que los dos lados no puedan decir cosas distintas.
export const GRASA_MAXIMA_DEFINICION = 14;

export const OBJETIVOS = [
    { clave: 'ganar_volumen', nombre: 'Ganar volumen', definicion: null },
    { clave: 'perder_grasa', nombre: 'Perder grasa', definicion: null },
    { clave: 'maxima_definicion', nombre: 'Máxima definición', definicion: `Bajar del ${GRASA_MAXIMA_DEFINICION} % de grasa.` },
    { clave: 'recomposicion', nombre: 'Recomposición', definicion: 'Empieza cuando termina una definición.' },
    { clave: 'mantenimiento', nombre: 'Mantenimiento', definicion: null },
    { clave: 'tonificacion', nombre: 'Tonificación', definicion: null },
];

/**
 * LOS QUE PUEDE PEDIR EL CLIENTE: los del cuestionario de alta, con sus palabras. Una sola
 * lista para el cuestionario (`goal`, con su `valor` viejo) y el mensual (28-10, Enrique:
 * «tantos botones como objetivos pueda seleccionar de acuerdo a su sexo y las posibilidades
 * del cuestionario de registro inicial»). Gana a los tres del 22-10 (con mantenimiento),
 * que es lo más antiguo. El entrenador sigue poniendo cualquiera de los seis desde la ficha.
 * Estos son los de hombre; desde el 31-10 los de mujer son `OBJETIVOS_DE_MUJER`.
 */
export const OBJETIVOS_DEL_CUESTIONARIO = [
    { clave: 'ganar_volumen', valor: 'volumen', texto: 'Quiero ganar masa muscular (VOLUMEN)' },
    { clave: 'perder_grasa', valor: 'definicion', texto: 'Quiero perder grasa (DEFINICIÓN)' },
];

// La base guarda los dos convenios de sexo: «mujer» (la app) y «female» (las clientas
// migradas de Calma). La misma tolerancia que el backend.
export const esMujer = (sexo) => {
    const s = String(sexo || '').toLowerCase();
    return s.startsWith('muj') || s.startsWith('fem') || s === 'f';
};

/**
 * LOS DE MUJER (31-10): tres, con estas palabras. El tercero es `tonificacion`, que para el
 * motor de macros es definición (`motor` en `core/objetivos.py`).
 */
export const OBJETIVOS_DE_MUJER = [
    { clave: 'ganar_volumen', valor: 'volumen', texto: 'Estoy delgada y quiero ganar masa muscular' },
    { clave: 'perder_grasa', valor: 'definicion', texto: 'Mi máxima prioridad es bajar kilos y perder grasa' },
    { clave: 'tonificacion', valor: 'tonificacion', texto: 'No tanto perder peso como tonificar o endurecer las zonas complicadas' },
];

export const objetivosDelCuestionario = (sexo) => (esMujer(sexo) ? OBJETIVOS_DE_MUJER : OBJETIVOS_DEL_CUESTIONARIO);

/** Tonificar solo existe en la lista de mujer: si cambia a hombre, hay que volver a preguntar. */
export const objetivoValePara = (goal, sexo) => goal !== 'tonificacion' || esMujer(sexo);

const POR_CLAVE = Object.fromEntries(OBJETIVOS.map((o) => [o.clave, o]));

// De los valores viejos (`goal`) al objetivo nuevo, literal: definición es perder grasa.
const DESDE_GOAL = {
    volumen: 'ganar_volumen', definicion: 'perder_grasa', perdida_grasa: 'perder_grasa',
    'perdida-grasa': 'perder_grasa', mantenimiento: 'mantenimiento',
    recomposicion: 'recomposicion', tonificacion: 'tonificacion',
};

export const normalizarObjetivo = (clave) => {
    const c = String(clave || '').trim().toLowerCase();
    if (POR_CLAVE[c]) return c;
    return DESDE_GOAL[c] || null;
};

/** «Perder grasa», o «» si no hay objetivo. Acepta también un `goal` viejo. */
export const nombreDelObjetivo = (clave) => POR_CLAVE[normalizarObjetivo(clave) || '']?.nombre || '';

export const definicionDelObjetivo = (clave) => POR_CLAVE[normalizarObjetivo(clave) || '']?.definicion || null;

/** El objetivo que se enseña al cliente: el actual de la ficha, y si no, el `goal` viejo traducido. */
export const objetivoVisible = (profile) => normalizarObjetivo(profile?.objetivo_actual || profile?.goal);
